import * as React from 'react';
import { FormSectionBase, FormSectionBaseState } from '../Types/FormSectionBase';
import { CheckBox } from 'sos-common/js/components/FormControls/CheckBox';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { FieldName, ApplicationType, FormType } from '../Types/Intyg.enums';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { AntalIntygPerSprak, IntygPerSprakModel } from './AntalIntygPerSprak';
import { ManuellaAdressuppgifter } from './ManuellaAdressuppgifter';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { ApiService } from '../Services/intyg.api.service';
import { IAppContext } from '../Types/Intyg.types';

export interface IAdressuppgifterProps {}
export interface IAdressuppgifterState extends FormSectionBaseState {
  antalManuellaAdressuppgifter: number;
}

export default class Adressuppgifter extends FormSectionBase<IAdressuppgifterProps, IAdressuppgifterState> {
  private _isFolkbokforingAvailable: boolean;
  private adressRef1: React.RefObject<ManuellaAdressuppgifter>;
  private adressRef2: React.RefObject<ManuellaAdressuppgifter>;
  private folkbokforingRef: React.RefObject<AntalIntygPerSprak>;

  constructor(props: IAdressuppgifterProps, context: IAppContext) {
    super(props, context);

    this.adressRef1 = React.createRef<ManuellaAdressuppgifter>();
    this.adressRef2 = React.createRef<ManuellaAdressuppgifter>();
    this.folkbokforingRef = React.createRef<AntalIntygPerSprak>();

    let antalManuellaAdressuppgifter = 0;
    let errors = this.emptyErrorsMap;

    const { folkbokforing } = this.context;
    function stringTruthy(str: string) {
      if (str && str.trim()) return true;
      return false;
    }

    this._isFolkbokforingAvailable =
      (folkbokforing && stringTruthy(folkbokforing.Utdelningsadress1)) ||
      (folkbokforing && stringTruthy(folkbokforing.Utdelningsadress2));

    if (!this._isFolkbokforingAvailable) {
      const { updateModelUsingKeyValue } = this.context;
      // updateModelUsingKeyValue('adresser', this.getInitialAddressObject(0));
      updateModelUsingKeyValue('skickaTillFolkbokforing', false);
      //antalManuellaAdressuppgifter = 1;
    } else {
      errors = this.validateSkickaTillFolkbokforing(errors);
      antalManuellaAdressuppgifter = this.manualAdressCount;
    }

    this.state = {
      errors,
      antalManuellaAdressuppgifter,
    };
  }

  componentDidMount = () => {
    //PBI 5444: https://tfs.sos.se/tfs/TFS-EXT/E-tj%C3%A4nst%20Beh%C3%B6righet/_workitems/edit/5444
    if (!this._isFolkbokforingAvailable) {
      this.addManuellaAdressuppgifter();
    }
  };

  public hasErrors = () => {
    // Check children
    const adressOneError = this.adressRef1?.current?.hasErrors();
    const adressTwoError = this.adressRef2?.current?.hasErrors();
    const folkbokforingError = this.folkbokforingRef?.current?.hasErrors();
    const myErrors = super.hasErrors();
    return adressOneError || adressTwoError || folkbokforingError || myErrors;
  };

  layoutFolkbokforingAvailable = () => {
    const { folkbokforing } = this.context;

    return (
      <div className="preamble">
        {folkbokforing?.CareOf && <div>{`c/o ${folkbokforing.CareOf}`}</div>}
        {folkbokforing?.Utdelningsadress1 && <div>{folkbokforing.Utdelningsadress1}</div>}
        {folkbokforing?.Utdelningsadress2 && <div>{folkbokforing.Utdelningsadress2}</div>}

        <div>
          {folkbokforing?.PostNr && <span>{folkbokforing.PostNr}</span>}{' '}
          {folkbokforing?.Postort && <span>{folkbokforing.Postort}</span>}
        </div>
      </div>
    );
  };

  initWebform = () => {
    const { updateModelUsingKeyValue } = this.context;
    updateModelUsingKeyValue('adresser', this.getInitialAddressObject(1), () => {
      this.setState({ antalManuellaAdressuppgifter: 1 });
    });
  };

  initEservice = () => {
    const errors = this.validateSkickaTillFolkbokforing();
    this.setState({ errors, antalManuellaAdressuppgifter: this.manualAdressCount });
  };

  getInitialAddressObject = (id: number) => {
    const { model } = this.context;
    let leveransAdressArrClone = model.adresser?.slice();
    if (!leveransAdressArrClone) leveransAdressArrClone = [];
    leveransAdressArrClone.push({
      adressrad1: null,
      adressrad2: null,
      adressrad3: null,
      adressrad4: null,
      adressrad5: null,
      antalEngelska: 0,
      antalSvenska: 0,
      id,
    });
    return leveransAdressArrClone;
  };

  get folkbokforingIntygPerSprakModel(): IntygPerSprakModel[] {
    const { model, formType } = this.context;
    const arr: IntygPerSprakModel[] = [];

    const pushSwedish = () => {
      arr.push({
        fieldName: FieldName.AntalSvenskaTillFolkbokforing,
        label: 'Svenska',
        value: model.antalSvenskaTillFolkbokforing,
      });
    };
    const pushEnglish = () => {
      arr.push({
        fieldName: FieldName.AntalEngelskaTillFolkbokforing,
        label: 'Engelska',
        value: model.antalEngelskaTillFolkbokforing,
      });
    };

    switch (formType) {
      case FormType.Intyg_for_aktuell_legitimation:
      case FormType.Intyg_om_AT_tjanstgoring:
        pushEnglish();
        break;
      case FormType.Artikel_33:
      case FormType.Intyg_for_legitimation_inom_EU:
        if (model.pdfSvenska) pushSwedish();
        if (model.pdfEngelska) pushEnglish();
        break;
      case FormType.Intyg_om_forskrivningsratt:
      case FormType.Intyg_om_kontaktlinsbehorighet:
        pushSwedish();
        break;
    }

    return arr;
  }

  get manualAdressCount(): number {
    const { model } = this.context;
    let manualAdressCount = 0;
    if (model.adresser?.length > 0) manualAdressCount = model.adresser.length;
    return manualAdressCount;
  }

  validateSkickaTillFolkbokforing = (stateErrors = this.state.errors) => {
    const { Translate } = this.context;
    const errors = new Map(stateErrors);
    const manualAdressCount = this.manualAdressCount;

    if (this.skickaTillFolkbokforingVisualValue && manualAdressCount === 0)
      errors.set(FieldName.SkickaTillFolkbokforing, { showMessage: true, errorMessage: Translate('error_no_address') });
    else errors.delete(FieldName.SkickaTillFolkbokforing);
    return errors;
  };

  addManuellaAdressuppgifter = () => {
    const { updateModelUsingKeyValue, applicationType, ansokanId } = this.context;
    const { antalManuellaAdressuppgifter } = this.state;
    const newAntal = antalManuellaAdressuppgifter + 1;

    const doUpdate = (id: number) => {
      this.setState(
        {
          antalManuellaAdressuppgifter: newAntal,
        },
        () => {
          updateModelUsingKeyValue('adresser', this.getInitialAddressObject(id), () => {
            let errors = this.state.errors;
            if (applicationType === ApplicationType.Eservice) errors = this.validateSkickaTillFolkbokforing();
            this.setState({ errors });
          });
        }
      );
    };

    if (applicationType === ApplicationType.Eservice) {
      ApiService.call.createAddress(ansokanId).then((backendId) => {
        doUpdate(backendId);
      });
    } else {
      doUpdate(antalManuellaAdressuppgifter);
    }
  };

  removeManuellaAdressuppgifter = () => {
    const { updateModelUsingKeyValue, forceComponentTreeReRender, model, applicationType, ansokanId } = this.context;
    const { antalManuellaAdressuppgifter } = this.state;
    const newAntal = antalManuellaAdressuppgifter - 1;

    if (applicationType === ApplicationType.Eservice) {
      const id = model.adresser[model.adresser.length - 1].id;
      ApiService.call.deleteAddress(ansokanId, id);
    }
    this.setState(
      {
        antalManuellaAdressuppgifter: newAntal,
      },
      () => {
        updateModelUsingKeyValue('adresser', model.adresser.slice(0, -1), () => {
          let errors = this.state.errors;
          if (applicationType === ApplicationType.Eservice) errors = this.validateSkickaTillFolkbokforing();
          this.setState({ errors }, () => {
            forceComponentTreeReRender();
          });
        });
      }
    );
  };

  // This function is only called for Eservices
  onChangeSkickaTillFolkbokforing = (field: FormField<boolean>) => {
    const { updateModelAsBatch, updateModelUsingKeyValue, forceComponentTreeReRender, applicationType } = this.context;

    // I need to flip the value of this checkbox because the backend model
    // is declared in a positive notation, whilst the UI is declared in a
    // negative notation.
    const skickaTillFolkbokforing = !field.value;

    if (skickaTillFolkbokforing) {
      updateModelUsingKeyValue(field.fieldName, skickaTillFolkbokforing, () => {
        const errors = this.validateSkickaTillFolkbokforing();
        this.setState({ errors }, () => {
          forceComponentTreeReRender();
        });
      });
    }
    if (!skickaTillFolkbokforing) {
      // Do not send to folkbokföring, reset these values to 0.
      updateModelAsBatch(
        [
          {
            key: field.fieldName,
            value: skickaTillFolkbokforing,
          },
          {
            key: FieldName.AntalSvenskaTillFolkbokforing,
            value: 0,
          },
          {
            key: FieldName.AntalEngelskaTillFolkbokforing,
            value: 0,
          },
        ],
        () => {
          if (applicationType === ApplicationType.Eservice) {
            const errors = this.validateSkickaTillFolkbokforing();
            this.setState({ errors }, () => {
              forceComponentTreeReRender();
            });
          }
        }
      );
    }
  };

  get skickaTillFolkbokforingVisualValue(): boolean {
    if (this.context.model.skickaTillFolkbokforing === false) return true;
    return false;
  }

  public render() {
    const { antalManuellaAdressuppgifter } = this.state;
    const { isInEditMode, Translate, model, applicationType } = this.context;

    return (
      <>
        <EpiserverProperty propertyName="HeadingAdressuppgifter" isInEditMode={isInEditMode}>
          <h2>{this.findProperty('HeadingAdressuppgifter', 'adressuppgifter')}</h2>
        </EpiserverProperty>
        <EpiserverProperty
          propertyName="TextAdressuppgifter"
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div
            className="m-b-2"
            dangerouslySetInnerHTML={setInnerHtml(
              this.findProperty('TextAdressuppgifter', 'angevarintygetskickas')
            )}></div>
        </EpiserverProperty>

        {this._isFolkbokforingAvailable && (
          <div className="m-b-4">
            {this.layoutFolkbokforingAvailable()}

            {model.skickaTillFolkbokforing !== false && (
              <div className="m-b-2">
                <AntalIntygPerSprak
                  ref={this.folkbokforingRef}
                  id={99999}
                  skickaTillFolkbokforing={true}
                  intygPerSprakModels={this.folkbokforingIntygPerSprakModel}
                />
              </div>
            )}

            <CheckBox
              label={Translate('annanadress')}
              fieldName={FieldName.SkickaTillFolkbokforing}
              onChange={this.onChangeSkickaTillFolkbokforing}
              value={this.skickaTillFolkbokforingVisualValue}
              errorMessage={this.getErrorMessage(FieldName.SkickaTillFolkbokforing)}
            />
          </div>
        )}
        {antalManuellaAdressuppgifter >= 1 && (
          <>
            <ManuellaAdressuppgifter
              key={
                model.adresser && model.adresser[0]
                  ? `manuellaadressuppgifter_${model.adresser[0].id}`
                  : 'manuellaadressuppgifter_0'
              }
              ref={this.adressRef1}
              id={model.adresser && model.adresser[0] ? model.adresser[0].id : 0}
            />
            {applicationType === ApplicationType.Eservice && antalManuellaAdressuppgifter === 1 && (
              <div className="delete-order">
                <button type="button" onClick={this.removeManuellaAdressuppgifter} className="stripped-button m-t-4">
                  Ta bort mottagare <Icon iconType="Paperbin" />
                </button>
              </div>
            )}
          </>
        )}
        {antalManuellaAdressuppgifter === 2 && (
          <>
            <h3 className="m-t-4">{Translate('adressytterligaremottagare')}</h3>
            <ManuellaAdressuppgifter
              key={
                model.adresser && model.adresser[1]
                  ? `manuellaadressuppgifter_${model.adresser[1].id}`
                  : 'manuellaadressuppgifter_1'
              }
              ref={this.adressRef2}
              id={model.adresser && model.adresser[1] ? model.adresser[1].id : 1}
            />
          </>
        )}
        {antalManuellaAdressuppgifter < 2 && (
          <button
            type="button"
            onClick={this.addManuellaAdressuppgifter}
            className="button button--white m-t-2 p-l-6 p-r-6">
            + Lägg till mottagare
          </button>
        )}
        {antalManuellaAdressuppgifter === 2 && (
          <div className="delete-order">
            <button type="button" onClick={this.removeManuellaAdressuppgifter} className="stripped-button m-t-4">
              Ta bort mottagare <Icon iconType="Paperbin" />
            </button>
          </div>
        )}
      </>
    );
  }
}
